import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import { usePersistedState } from '../../../hooks';
import { HygienistProps, UserAccountProps } from 'utils/types';
import { HygienistSkill } from 'utils/constants';
import { entities } from '../../../reduxStore';
import { Qualification } from '../ProfilePage/components/Qualification';
import { SimpleModal } from '../../../components/Modal';
import { StyledButton } from '../../../components/Buttons/DefaultButton/DefaultButton.styles';

const ForcedQualificationPage: React.FC = () => {
    const history = useHistory();
    const [, setUser] = usePersistedState<UserAccountProps>({ entityName: entities.user });
    const [hygienist, setHygienist] = usePersistedState<{
        skills: HygienistSkill[],
    } & HygienistProps>({
        entityName: entities.hygienist,
    });

    const [showBlockingModal, setShowBlockingModal] = useState(true);
    const [showQualification, setShowQualification] = useState(false);

    const roleCopy = hygienist?.type === 1 ? 'Assisting' : 'Hygiene';

    const handleOkay = useCallback(() => {
        setShowBlockingModal(false);
        setShowQualification(true);
    }, []);

    const handleClose = useCallback(() => {
        setShowQualification(false);
        setShowBlockingModal(true);
    }, []);

    const handleSaveSuccess = useCallback((user: UserAccountProps) => {
        // Qualification supplies a verified snapshot only after every write and
        // read-back succeeds. Persist it before leaving the blocking form.
        safeLocalStorage.setItem('userAccount', JSON.stringify(user));
        (window as any).TempStars?.User?.setCurrentUser?.(user);
        const pageData = safeLocalStorage.getItem('pageData');
        if (pageData) {
            let page: string | undefined;
            try { page = JSON.parse(pageData)?.page; } catch { /* Ignore an unrelated malformed navigation hint. */ }
            if (page === 'forcedQualification') safeLocalStorage.removeItem('pageData');
        }
        setHygienist(user.hygienist as { skills: HygienistSkill[] } & HygienistProps);
        setUser(user);
        setShowQualification(false);
        history.replace({ pathname: 'hygienistHome', state: { qualificationSaved: true } });
    }, [history, setHygienist, setUser]);

    if (!hygienist) return null;

    return (
        <div style={{ height: '100%', background: '#f8f9fa' }}>
            <SimpleModal isOpen={showBlockingModal}>
                <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0B4598" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#222', margin: '0 0 12px' }}>
                        Confirm Your Qualification
                    </h2>
                    <p style={{ fontSize: 15, color: '#555', lineHeight: 1.6, margin: '0 0 24px' }}>
                        Before you go any further, we just need you to confirm your Qualification level for <strong>{roleCopy}</strong>.
                    </p>
                    <StyledButton
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleOkay}
                        data-testid="forced-qualification-continue-button"
                        style={{ width: '100%', fontWeight: 600, fontSize: 16 }}
                    >
                        Okay!
                    </StyledButton>
                </div>
            </SimpleModal>

            <Qualification
                isOpen={showQualification}
                hygienist={hygienist}
                onClose={handleClose}
                onSaveSuccess={handleSaveSuccess}
                hideCloseButton={true}
            />
        </div>
    );
};

export default ForcedQualificationPage;
